import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { isOnline } from "@/offline/network";
import { reloadPage } from "@/utils/reloadPage";

export const RELOAD_STORAGE_KEY = "sendstats:chunk-reload-at";

// A second failure this soon after a reload means the reload didn't help
// (the chunk really is gone, or something else is wrong), so stop retrying.
const RELOAD_WINDOW_MS = 10_000;

// Returns false when the reload can't be recorded: without that record, a
// chunk that's really missing would reload in a loop.
function claimReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STORAGE_KEY));
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/**
 * Runs a dynamic import, reloading the page once if it fails.
 *
 * A deploy replaces the content-hashed chunks, so a tab still running the
 * previous build gets a missing file the next time it lazy-loads a page.
 * Reloading picks up the new build. While the reload is in flight the
 * returned promise never settles, so Suspense keeps showing its fallback
 * instead of the error boundary.
 *
 * @param load - The dynamic import, e.g. `() => import("./pages/Foo")`.
 * @returns The loaded module.
 * @throws The original import error when offline, when a reload already
 *   happened within the last few seconds, or when sessionStorage is
 *   unavailable. That error reaches the error boundary and Sentry as before.
 */
export async function loadWithReload<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (err) {
    // Offline, a reload would just land on the browser's offline page.
    if (!(await isOnline()) || !claimReload()) throw err;
    reloadPage();
    return new Promise<T>(() => {});
  }
}

/**
 * `React.lazy` that reloads the page once when the chunk fails to load.
 * The `any` props bound matches React.lazy's own signature.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => loadWithReload(load));
}
